<template>
  <div class="mt-8" v-show="showDetails">
    <dispute-notification v-if="stageDispute">
      {{ stageDispute.comment }}
    </dispute-notification>
    <h3 class="font-light opacity-80 mb-5">REGISTERED BUSINESS ADDRESS</h3>

    <el-form
      label-position="top"
      :model="kycDetailsForm"
      ref="kycDetailsForm"
      :rules="rules"
      @submit.native.prevent="validateForm('kycDetailsForm')"
    >
      <div class="flex flex-col gap-1 bg-white">
        <el-form-item prop="registeredCountry">
          <p>Country<span class="text-error-10">*</span></p>
          <el-select
            class="w-full"
            filterable
            remote
            v-model="kycDetailsForm.registeredCountry"
            placeholder="Enter country"
            :remote-method="remoteMethod"
          >
            <el-option
              v-for="(country, index) in options"
              :key="index"
              :label="country.name"
              :value="country.code2"
            >
              {{ country.name }}
            </el-option>
          </el-select>
        </el-form-item>

        <div class="flex flex-col md:flex-row gap-6">
          <el-form-item prop="registeredState" class="w-full md:w-1/2">
            <p>State <span class="text-error-10">*</span></p>
            <el-select v-model="kycDetailsForm.registeredState">
              <el-option
                v-for="(state, index) in GetStatesForCountry(
                  kycDetailsForm.registeredCountry
                )"
                :key="index"
                :label="state.name"
                :value="state.name"
              >
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item prop="registeredCity" class="w-full md:w-1/2">
            <p>City <span class="text-error-10">*</span></p>
            <el-input v-model="kycDetailsForm.registeredCity"></el-input>
          </el-form-item>
        </div>
        <div class="flex flex-col md:flex-row gap-6">
          <el-form-item
            prop="registeredBusinessAddress"
            class="w-full md:w-1/2"
          >
            <p>Street <span class="text-error-10">*</span></p>
            <el-input
              v-model="kycDetailsForm.registeredBusinessAddress"
            ></el-input>
          </el-form-item>
          <el-form-item prop="registeredPostalCode" class="w-full md:w-1/2">
            <p>Postal Code <span class="text-error-10">*</span></p>
            <el-input v-model="kycDetailsForm.registeredPostalCode"></el-input>
          </el-form-item>
        </div>

        <h3 class="font-light opacity-80 mb-1">OPERATIONAL ADDRESS</h3>
        <el-checkbox
          v-model="sameAsBusinessAddress"
          @change="handleChange"
          class="mb-4"
          >Same as Registered Business Address</el-checkbox
        >
        <div v-if="!sameAsBusinessAddress">
          <el-form-item prop="countryOfOperation">
            <p>Country<span class="text-error-10">*</span></p>
            <el-select
              class="w-full"
              filterable
              remote
              v-model="kycDetailsForm.countryOfOperation"
              placeholder="Enter country"
              :remote-method="remoteMethod"
            >
              <el-option
                v-for="(country, index) in options"
                :key="index"
                :label="country.name"
                :value="country.code2"
              >
                {{ country.name }}
              </el-option>
            </el-select>
          </el-form-item>

          <div class="flex flex-col md:flex-row gap-6">
            <el-form-item prop="stateOfOperation" class="w-full md:w-1/2">
              <p>State <span class="text-error-10">*</span></p>
              <el-select v-model="kycDetailsForm.stateOfOperation">
                <el-option
                  v-for="(state, index) in GetStatesForCountry(
                    kycDetailsForm.countryOfOperation
                  )"
                  :key="index"
                  :label="state.name"
                  :value="state.name"
                >
                </el-option>
              </el-select>
            </el-form-item>

            <el-form-item prop="cityOfOperation" class="w-full md:w-1/2">
              <p>City <span class="text-error-10">*</span></p>
              <el-input v-model="kycDetailsForm.cityOfOperation"></el-input>
            </el-form-item>
          </div>
          <div class="flex flex-col md:flex-row gap-6">
            <el-form-item prop="addressOfOperation" class="w-full md:w-1/2">
              <p>Street <span class="text-error-10">*</span></p>
              <el-input v-model="kycDetailsForm.addressOfOperation"></el-input>
            </el-form-item>
            <el-form-item prop="postalCodeOfOperation" class="w-full md:w-1/2">
              <p>Postal Code <span class="text-error-10">*</span></p>
              <el-input
                v-model="kycDetailsForm.postalCodeOfOperation"
              ></el-input>
            </el-form-item>
          </div>
        </div>

        <button
          class="text-white bg-primary rounded-lg mt-2 h-12 md:h-14 w-44 md:w-52 hover:opacity-90"
          :disabled="isLoading || isApproved"
          type="submit"
        >
          <spinner
            v-if="isLoading"
            size="small"
            :spacing="40"
            :line-fg-color="$primaryColor"
          />
          <span v-else>Confirm</span>
        </button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { ALL_COUNTRIES } from '@/utils/constants.js'
import submitKycDetailsStep from '@/mixins/kyc.js'
import { ValidateKycInput, GetStatesForCountry } from '@/utils/helpers.js'

export default {
  name: 'EntityAddress',
  mixins: [submitKycDetailsStep],
  components: {
    DisputeNotification: () =>
      import('@/components/v2/completeKycForm/common/DisputeNotification.vue')
  },
  props: {
    business: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      sameAsBusinessAddress: false,
      isLoading: false,
      options: [],
      allCountries: ALL_COUNTRIES,
      formData: new FormData(),
      kycStep: 3,
      kycDetailsForm: {
        registeredBusinessAddress: '',
        registeredCountry: '',
        registeredState: '',
        registeredCity: '',
        registeredPostalCode: '',
        stateOfOperation: '',
        postalCodeOfOperation: '',
        cityOfOperation: '',
        countryOfOperation: '',
        addressOfOperation: ''
      },
      rules: {
        registeredBusinessAddress: [
          {
            required: true,
            message: 'Please enter business address of registration',
            trigger: ['blur', 'change']
          },
          { validator: ValidateKycInput, trigger: ['blur', 'change'] }
        ],
        registeredState: [
          {
            required: true,
            message: 'Please enter business state of registration',
            trigger: ['blur', 'change']
          }
        ],
        registeredCountry: [
          {
            required: true,
            message: 'Please enter business country of registration',
            trigger: ['blur', 'change']
          }
        ],
        registeredPostalCode: [
          {
            required: true,
            message: 'Please enter business postal code of registration',
            trigger: ['blur', 'change']
          },
          {
            max: 6,
            message: 'Invalid postal code',
            trigger: ['blur', 'change']
          }
        ],
        registeredCity: [
          {
            required: true,
            message: 'Please enter business city of registration',
            trigger: ['blur', 'change']
          },
          { validator: ValidateKycInput, trigger: ['blur', 'change'] }
        ],
        addressOfOperation: [
          {
            required: true,
            message: 'Please enter full business address of operation',
            trigger: ['blur', 'change']
          },
          { validator: ValidateKycInput, trigger: ['blur', 'change'] }
        ],
        stateOfOperation: [
          {
            required: true,
            message: 'Please enter business state of operation',
            trigger: ['blur', 'change']
          }
        ],
        countryOfOperation: [
          {
            required: true,
            message: 'Please enter business country of operation',
            trigger: ['blur', 'change']
          }
        ],
        postalCodeOfOperation: [
          {
            required: true,
            message: 'Please enter business postal code of operation',
            trigger: ['blur', 'change']
          },
          {
            max: 6,
            message: 'Invalid postal code',
            trigger: ['blur', 'change']
          }
        ],
        cityOfOperation: [
          {
            required: true,
            message: 'Please enter business city of operation',
            trigger: ['blur', 'change']
          },
          { validator: ValidateKycInput, trigger: ['blur', 'change'] }
        ]
      }
    }
  },
  computed: {
    showDetails () {
      return this.business.show && this.business.step === this.kycStep
    },
    entityInfo () {
      return this.$store.state.kyc.kycDetails
    },
    isApproved () {
      return this.approvedStages.includes(this.business.step)
    }
  },
  methods: {
    GetStatesForCountry,
    sameAsRegisteredHandler () {
      this.kycDetailsForm.countryOfOperation =
        this.kycDetailsForm.registeredCountry
      this.kycDetailsForm.stateOfOperation = this.kycDetailsForm.registeredState
      this.kycDetailsForm.cityOfOperation = this.kycDetailsForm.registeredCity
      this.kycDetailsForm.addressOfOperation =
        this.kycDetailsForm.registeredBusinessAddress
      this.kycDetailsForm.postalCodeOfOperation =
        this.kycDetailsForm.registeredPostalCode
    },
    notSameAsRegisteredHandler () {
      this.kycDetailsForm.countryOfOperation = ''
      this.kycDetailsForm.stateOfOperation = ''
      this.kycDetailsForm.cityOfOperation = ''
      this.kycDetailsForm.addressOfOperation = ''
      this.kycDetailsForm.postalCodeOfOperation = ''
    },
    handleChange (val) {
      if (val) {
        this.sameAsRegisteredHandler()
      } else {
        this.notSameAsRegisteredHandler()
      }
    },
    validateForm (kycDetailsForm) {
      this.$refs[kycDetailsForm].validate((valid) => {
        if (valid) {
          if (this.sameAsBusinessAddress) this.sameAsRegisteredHandler()
          this.submitKycStepThree()
        } else {
          this.$notify.error({
            title: 'Incomplete details',
            message: 'Please fill the missing information',
            position: 'top-right'
          })
        }
      })
    },
    submitKycStepThree () {
      const {
        registeredBusinessAddress,
        registeredCountry,
        registeredState,
        registeredPostalCode,
        addressOfOperation,
        countryOfOperation,
        stateOfOperation,
        postalCodeOfOperation,
        cityOfOperation,
        registeredCity
      } = this.kycDetailsForm

      const entityAddress = {
        operational: {
          businessAddress: addressOfOperation,
          countryOfOperation: countryOfOperation,
          state: stateOfOperation,
          postalCode: postalCodeOfOperation,
          city: cityOfOperation
        },
        registered: {
          businessAddress: registeredBusinessAddress,
          countryOfOperation: registeredCountry,
          state: registeredState,
          postalCode: registeredPostalCode,
          city: registeredCity
        }
      }
      const payload = {
        kycDetailsForm: { entityAddress },
        kycStep: this.kycStep
      }
      this.submitKycDetailsStep(payload)
    },
    prefillForm () {
      const entity = this.entityInfo.contacts.find(
        (item) => item.contactType === 'company'
      )

      this.kycDetailsForm = {
        // Registered address fields
        registeredBusinessAddress:
          entity?.registeredAddress?.business_address || '',
        registeredCountry:
          entity?.registeredAddress?.country_of_operation || '',
        registeredState: entity?.registeredAddress?.state || '',
        registeredPostalCode: entity?.registeredAddress?.postal_code || '',
        registeredCity: entity?.registeredAddress?.city || '',
        // Operational address fields
        addressOfOperation: entity?.operationalAddress?.business_address || '',
        countryOfOperation:
          entity?.operationalAddress?.country_of_operation || '',
        stateOfOperation: entity?.operationalAddress?.state || '',
        postalCodeOfOperation: entity?.operationalAddress?.postal_code || '',
        cityOfOperation: entity?.operationalAddress?.city || ''
      }
    }
  },
  mounted () {
    if (this.entityInfo.contacts && this.entityInfo.contacts.length > 0) {
      this.prefillForm()
    }
  },
  watch: {
    'kycDetailsForm.registeredCountry' (newValue, oldValue) {
      this.kycDetailsForm.registeredState = ''
    },
    'kycDetailsForm.countryOfOperation' (newValue, oldValue) {
      this.kycDetailsForm.stateOfOperation = ''
    }
  }
}
</script>

<style scoped lang="scss">
p {
  color: #5d5e68;
  font-size: 13px;
}
button:disabled {
  opacity: 0.6;
  cursor: default;
}
:deep(.el-checkbox__inner) {
  height: 1.3rem;
  width: 1.3rem;
}
:deep(.el-checkbox__inner::after) {
  height: 0.6rem;
  left: 0.4rem;
  border-width: 2px;
  top: 0.15rem;
}
:deep(.el-checkbox__label) {
  font-weight: 300;
  font-size: 1rem;
}
:deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
  color: var(--color-primary);
}
</style>
